import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import ProgramCanvas from "@/components/studio/ProgramCanvas";
import { useRoster, useStableSpeaker } from "@/hooks/useRtkMeeting";
import { useRecorderLink } from "@/hooks/useRecorderLink";
import { useShowSocket } from "@/hooks/useShowSocket";
import { studioApi } from "@/lib/studioApi";

/**
 * Program Output — the page Cloudflare's headless recorder captures.
 *
 * RealtimeKit loads  /program/{showId}/{key}?authToken=...&config=...  inside a
 * container Chrome, we join the room as the (hidden) recorder participant and
 * render the ProgramCanvas full-screen. Whatever this page shows is the MP4 /
 * live stream. With no `authToken` in the URL a signed-in producer gets a
 * preview token so they can open the page in a normal tab to check it.
 * The room link heals itself (see useRecorderLink); the job never stops on its own.
 */
export default function ProgramOutput() {
  const { showId, key } = useParams();
  const [search] = useSearchParams();
  const tokenFromRecorder = search.get("authToken");
  const isRecorder = !!tokenFromRecorder;
  const [authToken, setAuthToken] = useState(tokenFromRecorder || null);
  const [error, setError] = useState(null);
  const [initial, setInitial] = useState(null);

  const { show: liveShow } = useShowSocket(showId, { programKey: key });
  const show = liveShow || initial;

  // Link events go to the console's monitor log; queued while we are offline, flushed when back.
  const queue = useRef([]);
  const flushing = useRef(false);
  const flush = useCallback(async () => {
    if (flushing.current) return;
    flushing.current = true;
    while (queue.current.length) {
      try { await studioApi.programEvent(showId, key, queue.current[0]); queue.current.shift(); } catch (e) { break; }
    }
    flushing.current = false;
  }, [showId, key]);
  const onEvent = useCallback((event, detail) => {
    if (!isRecorder) return;
    queue.current.push({ event, detail, at: new Date().toISOString() });
    if (queue.current.length > 50) queue.current.shift();
    flush();
  }, [isRecorder, flush]);
  useEffect(() => {
    window.addEventListener("online", flush);
    return () => window.removeEventListener("online", flush);
  }, [flush]);
  const { meeting, joined, error: linkError, attempt, linkDown, onFlow } = useRecorderLink(authToken, { recorder: isRecorder, onEvent });
  const roster = useRoster(meeting);
  const activeSpeaker = useStableSpeaker(meeting, roster, show?.participants || [], show?.program);

  // Black page, no scrollbars — this IS the video frame.
  useEffect(() => {
    const prev = { bg: document.body.style.background, overflow: document.body.style.overflow };
    document.body.style.background = "#000";
    document.body.style.overflow = "hidden";
    return () => { document.body.style.background = prev.bg; document.body.style.overflow = prev.overflow; };
  }, []);

  // Initial show data (WS snapshot arrives right after, but don't wait on it).
  useEffect(() => {
    studioApi.programData(showId, key).then(setInitial).catch((e) => setError(e?.response?.data?.detail || "Show not found"));
  }, [showId, key]);

  // Preview mode: mint a token for the signed-in producer.
  useEffect(() => {
    if (authToken) return;
    studioApi.previewToken(showId, key)
      .then((r) => setAuthToken(r.auth_token))
      .catch(() => setError("Sign in as a producer to preview the program output."));
  }, [authToken, showId, key]);

  const problem = error || linkError;
  if (problem) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black text-center text-white" data-testid="program-error">
        <div>
          <p className="text-lg font-semibold">Program output unavailable</p>
          <p className="mt-2 text-sm text-white/70">{problem}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black" data-testid="program-output" data-joined={joined ? "1" : "0"} data-link={linkDown ? "down" : "up"} data-attempt={attempt}>
      <ProgramCanvas
        meeting={meeting}
        roster={roster}
        activeSpeaker={activeSpeaker}
        program={show?.program}
        participants={show?.participants || []}
        showTitle={show?.title}
        scheduledFor={show?.scheduled_for}
        withAudio
        onFlow={onFlow}
        className="h-full w-full"
      />
      {!isRecorder && (
        <div className="pointer-events-none absolute left-3 top-3 flex items-center gap-2 rounded bg-black/60 px-2 py-1 text-[11px] font-semibold uppercase tracking-widest text-white/80" data-testid="program-preview-badge">
          Preview
          {attempt > 0 && <span className="rounded bg-amber-500/90 px-1.5 text-[10px] text-black" data-testid="program-reconnecting">reconnecting · try {attempt}</span>}
        </div>
      )}
    </div>
  );
}
